import { z } from "zod";

export const Id = z.string().uuid();
export const IsoDate = z.string().datetime({ offset: true });
export const Locale = z.enum(["en", "ar"]);

export const Page = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });
/**
 * Boolean query parameter: accepts true/false and the strings "true"/"false"/"1"/"0".
 * (`z.coerce.boolean()` turns the string "false" into true — never use it for query flags.)
 */
export const QueryBool = z.union([z.boolean(), z.enum(["true", "false", "1", "0"])]).transform((v) => v === true || v === "true" || v === "1");

export const PageQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** RFC 9457 problem details. */
export const Problem = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Stable failure codes the worker stores on operations / messages / approvals (`errorCode`), ALP-77. The UI shows one
 * localized message + action per code (Reconnect for `channel_reauth_required`, "the customer must message first" for
 * `messaging_window_closed`, Retry for `rate_limited` / `provider_error`). `unknown` = legacy rows without a code.
 */
export const OPERATION_ERROR_CODES = [
  "channel_reauth_required", "messaging_window_closed", "rate_limited", "provider_error", "validation", "capability_not_supported",
  "not_found", "forbidden", "outbound_disabled", "conflict", "limit_reached", "feature_not_in_plan",
  // AI jobs (ai-jobs.ts)
  "ai_unavailable", "budget_exceeded", "storage_error", "content_policy", "timeout", "unknown",
] as const;
export type OperationErrorCode = (typeof OPERATION_ERROR_CODES)[number];

/** 202 Accepted for async work; progress arrives over SSE (`operation.updated`). */
export const Operation = z.object({
  id: Id,
  kind: z.string(),
  status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  progress: z.object({ done: z.number(), total: z.number().nullable(), failed: z.number() }).optional(),
  result: z.unknown().optional(),
  error: z.string().optional(),
  /** Stable failure code, e.g. `channel_reauth_required` (the UI offers "Reconnect"), ALP-77. */
  errorCode: z.string().optional(),
  createdAt: IsoDate,
});

export const Metric = z.object({ key: z.string(), value: z.number().nullable(), previous: z.number().nullable().optional() });
export const SeriesPoint = z.object({ t: IsoDate, v: z.number() });

export type Page<T> = { items: T[]; nextCursor: string | null };
export type Problem = z.infer<typeof Problem>;
export type Operation = z.infer<typeof Operation>;
